import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  FileExcelOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  InputNumber,
  List,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from '../HowToCard';
import { useGoToSection } from '../howToLinks';
import { saveBlob } from '../format';
import PackSettings from './PackSettings';
import ReleasesPanel from './ReleasesPanel';

const { Text } = Typography;

/**
 * The month's reports for the client, in AB's own formats: one download per
 * file, or all of them as a ZIP, and the settings of what the client gets.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @returns {JSX.Element} The panel
 */
export default function PackPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  const [period, setPeriod] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const [fxRate, setFxRate] = useState(null);
  const year = period.year();
  const month = period.month() + 1;
  const params = { year, month, fxRate: fxRate || undefined };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['client-reports-pack', companyId, year, month, fxRate],
    queryFn: () => API.clientReports.getReportPack(companyId, params),
  });
  const pack = data?.data;
  const [openSections, setOpenSections] = useState(['releases']);
  useGoToSection('pack', (key) => setOpenSections((keys) => [...new Set([...keys, key])]));

  const onError = (e) => {
    // Blob responses carry the server's JSON error as a blob.
    const blob = e?.response?.data;
    if (blob instanceof Blob) {
      blob.text().then((text) => {
        try {
          message.error(JSON.parse(text).message);
        } catch {
          message.error(e.message);
        }
      });
      return;
    }
    message.error(e?.response?.data?.message || e.message);
  };

  const fileMutation = useMutation({
    mutationFn: (title) =>
      API.clientReports.exportReportFile(companyId, { ...params, title }),
    onSuccess: saveBlob,
    onError,
  });
  const zipMutation = useMutation({
    mutationFn: () => API.clientReports.exportReportPackZip(companyId, params),
    onSuccess: saveBlob,
    onError,
  });

  const noLedger = pack && !pack.sync;

  return (
    <div>
      <HowToCard guide="pack" open={pack ? !pack.files.length : undefined} />
      <Space id="howto-pack-toolbar" wrap style={{ marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          format="MM.YYYY"
          value={period}
          onChange={(value) => value && setPeriod(value.startOf('month'))}
        />
        <Button
          type="primary"
          className="btn-primary"
          icon={<FileZipOutlined />}
          disabled={!pack?.files?.length || noLedger}
          loading={zipMutation.isPending}
          onClick={() => zipMutation.mutate()}
        >
          {t('pack.downloadAll')}
        </Button>
      </Space>

      {noLedger && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('fccs.noLedger', { year })}
        />
      )}

      {pack?.fxCurrency && (
        <Space wrap style={{ marginBottom: 12, marginLeft: 24 }}>
          <Text>
            {pack.fx
              ? t('pack.fxRate', {
                  currency: pack.fx.currency,
                  date: dayjs(pack.fx.date).format('DD.MM.YYYY'),
                  rate: pack.fx.rate,
                  source:
                    pack.fx.source === 'nbs'
                      ? t('pack.fxNbs')
                      : t('pack.fxManual'),
                })
              : null}
          </Text>
          {pack.fxError && <Text type="danger">{t('pack.fxMissing')}</Text>}
          <InputNumber
            placeholder={t('pack.fxOverride')}
            min={0}
            step={0.0001}
            style={{ width: 180 }}
            value={fxRate}
            onChange={setFxRate}
          />
        </Space>
      )}

      {pack && !pack.files.length && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('pack.empty')}
        />
      )}

      {pack?.files?.length > 0 && (
        <List
          bordered
          loading={isLoading}
          dataSource={pack.files}
          style={{ marginBottom: 16 }}
          renderItem={(file) => (
            <List.Item
              actions={[
                <Button
                  key="download"
                  shape="round"
                  icon={<DownloadOutlined />}
                  disabled={noLedger || file.reports.every((r) => r.pending)}
                  loading={
                    fileMutation.isPending &&
                    fileMutation.variables === file.title
                  }
                  onClick={() => fileMutation.mutate(file.title)}
                >
                  {t('pack.download')}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <FileExcelOutlined
                    style={{ fontSize: 20, color: '#1d6f42' }}
                  />
                }
                title={file.fileName}
                description={
                  <Space size={4} wrap>
                    {file.reports.map((r, i) => (
                      <Tag key={i} color={r.pending ? 'default' : 'blue'}>
                        {t(`pack.types.${r.type}`)}
                        {['analyticTrialBalance', 'generalLedger'].includes(
                          r.type
                        ) && ` · ${t(`pack.period.${r.period}`)}`}
                        {r.pending && ` · ${t('pack.pending')}`}
                      </Tag>
                    ))}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}

      <Collapse
        activeKey={openSections}
        onChange={setOpenSections}
        items={[
          {
            key: 'releases',
            label: t('release.title'),
            children: (
              <div id="howto-pack-releases">
                <ReleasesPanel
                  companyId={companyId}
                  period={period}
                  fxRate={fxRate}
                  canPublish={Boolean(pack?.files?.length) && !noLedger}
                />
              </div>
            ),
          },
          {
            key: 'settings',
            label: t('pack.settings'),
            children: (
              <div id="howto-pack-settings">
                <PackSettings companyId={companyId} onSaved={refetch} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
